"use client";

import { useEffect, useRef, useState } from "react";
import { loadModelViewer } from "@/lib/utils/loadModelViewer";

/**
 * Load model-viewer script only when the container enters (or nears) the viewport.
 * @param {{ enabled?: boolean, rootMargin?: string }} [options]
 */
export function useModelViewerWhenVisible({ enabled = true, rootMargin = "200px" } = {}) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || ready) return;

    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;

    const startLoad = () => {
      loadModelViewer()
        .then(() => {
          if (!cancelled) setReady(true);
        })
        .catch(() => {});
    };

    if (typeof IntersectionObserver === "undefined") {
      startLoad();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          startLoad();
        }
      },
      { rootMargin }
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [enabled, ready, rootMargin]);

  return { containerRef, ready };
}
